import { Bar } from 'react-chartjs-2';
import { ICurrentData, IOrderDataProps } from '../OrderBarChart';
import { useTranslation } from 'react-i18next';
import { orderChartOptions } from '../orderChartOptions';

interface GroupedDataItem {
  data: ICurrentData[];
  totalStats: {
    canceled: number;
    created: number;
    success: number;
    cancel_by_admin: number;
    cancel_by_user: number;
    cancel_by_system: number;
  };
}

interface IGeneralBarProps {
  orderData: IOrderDataProps | undefined;
  setSelectedData: (label: string) => void;
  setCurrentData: (data: ICurrentData[]) => void;
}

export const GeneralBar = ({
  orderData,
  setSelectedData,
  setCurrentData,
}: IGeneralBarProps) => {
  const { t } = useTranslation();

  const groupedDataArray = Object.entries(orderData?.statistic || {}).reduce(
    (acc: Record<string, GroupedDataItem>, [key, value]) => {
      const prefix = key.split(' ')[0];

      if (!acc[prefix]) {
        acc[prefix] = {
          data: [],
          totalStats: {
            canceled: 0,
            created: 0,
            success: 0,
            cancel_by_admin: 0,
            cancel_by_user: 0,
            cancel_by_system: 0,
          },
        };
      }

      acc[prefix].totalStats.canceled += value.statuses.canceled;
      acc[prefix].totalStats.created += value.statuses.created;
      acc[prefix].totalStats.success += value.statuses.success;
      acc[prefix].totalStats.cancel_by_admin += value.statuses.cancel_by_admin;
      acc[prefix].totalStats.cancel_by_user += value.statuses.cancel_by_user;
      acc[prefix].totalStats.cancel_by_system +=
        value.statuses.cancel_by_system;

      acc[prefix].data.push({
        label: key,
        data: {
          all_count: value.all_count,
          statistic: value.statuses,
        },
      });

      return acc;
    },
    {}
  );

  const groupedDataEntries: [string, GroupedDataItem][] =
    Object.entries(groupedDataArray);

  const sortedGroupedData = groupedDataEntries.sort((a, b) => {
    const aTotal =
      a[1].totalStats.canceled +
      a[1].totalStats.created +
      a[1].totalStats.success;
    const bTotal =
      b[1].totalStats.canceled +
      b[1].totalStats.created +
      b[1].totalStats.success;
    return bTotal - aTotal;
  });

  const stackedData = {
    labels: sortedGroupedData.map(([prefix]) => prefix),
    datasets: [
      {
        label: t('cancelled'),
        data: sortedGroupedData.map(([, data]) => data.totalStats.canceled),
        backgroundColor: 'rgba(255, 188, 188, 1)',
        hoverBackgroundColor: 'rgba(255, 99, 132, 1)',
        borderRadius: 8,
        barPercentage: 0.7,
      },
      {
        label: t('created_by'),
        data: sortedGroupedData.map(([, data]) => data.totalStats.created),
        backgroundColor: 'rgba(142, 193, 255, 1)',
        hoverBackgroundColor: 'rgba(54, 162, 235, 1)',
        borderRadius: 8,
        barPercentage: 0.7,
      },
      {
        label: t('completed'),
        data: sortedGroupedData.map(([, data]) => data.totalStats.success),
        backgroundColor: 'rgba(169, 233, 197, 1)',
        hoverBackgroundColor: 'rgba(75, 192, 192, 1)',
        borderRadius: 8,
        barPercentage: 0.7,
      },
    ],
  };

  const compatibleData: [
    string,
    {
      all_count: number;
      statuses: {
        canceled: number;
        created: number;
        success: number;
        cancel_by_admin: number;
        cancel_by_user: number;
        cancel_by_system: number;
      };
    },
  ][] = sortedGroupedData.map(([prefix, data]) => [
    prefix,
    {
      all_count:
        data.totalStats.canceled +
        data.totalStats.created +
        data.totalStats.success,
      statuses: {
        ...data.totalStats,
      },
    },
  ]);

  const handleClick = (
    _: any,
    elements: { datasetIndex: number; index: number }[]
  ) => {
    if (elements.length > 0) {
      const element = elements[0];
      const dataIndex = element.index;
      const label = stackedData.labels[dataIndex];
      const current = sortedGroupedData.find(item => item[0] === label);
      const currentData = current ? (current[1].data as ICurrentData[]) : [];

      setSelectedData(label);
      setCurrentData(currentData);
    }
  };

  const options = {
    ...orderChartOptions(compatibleData),
    onClick: handleClick,
  };

  return <Bar data={stackedData} options={options} />;
};
